import { Minus, Plus } from 'lucide-react';
import { useRef, useState, type KeyboardEvent, type ReactNode, type ComponentProps } from 'react';
import { cn } from '@/lib/infra/utils';
import type { UsRailControls } from './UsManualTicket';
import { decimalUnits, formatUnits, localExpiryToUtc, utcExpiryToLocal } from '@/lib/polymarketUs/ticketModel';
import type { UsSelectedMarket } from '@/lib/polymarketUs/marketClient';
import type { UsEditTermNote } from '@/lib/polymarketUs/ticketEdit';
import {
  durationOptions,
  GTD,
  gtsAllowedFor,
  startExpiryText,
  stepPrice,
  type UsTicketCheck,
  type UsTicketEntry,
  type UsTicketField,
} from '@/lib/polymarketUs/ticketModel';

const choice = (active: boolean, tone?: 'positive' | 'negative') =>
  cn(
    'min-w-0 flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors break-words [overflow-wrap:anywhere]',
    active
      ? tone === 'negative'
        ? 'bg-negative text-white'
        : tone === 'positive'
          ? 'bg-positive text-white'
          : 'bg-primary text-primary-foreground'
      : 'bg-muted text-muted-foreground hover:text-foreground',
    'aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
  );

/** Keep partial text (such as 66.) while converting only exact cents to USD.
 * Invalid/over-precision input stays visible and invalid; never round an order. */
function CentsInput({ value, onPriceChange, ...props }: Omit<ComponentProps<'input'>, 'value' | 'onChange'> & {
  value: string; onPriceChange(value: string): void;
}) {
  const display = (usd: string) => { const n = decimalUnits(usd); return n === null ? usd : formatUnits(n * 100n); };
  const [draft, setDraft] = useState(() => ({ usd: value, text: display(value) }));
  let text = draft.text;
  if (draft.usd !== value) {
    text = display(value);
    setDraft({ usd: value, text });
  }
  return <input {...props} value={text} onChange={event => {
    const raw = event.target.value;
    const n = decimalUnits(raw);
    const usd = raw === '' ? '' : n !== null && n % 100n === 0n ? formatUnits(n / 100n) : 'invalid';
    setDraft({ usd, text: raw });
    onPriceChange(usd);
  }} />;
}

/** A named outcome: the market's own label when it has one, else YES/NO. */
export function outcomeName(market: UsSelectedMarket, outcome: 'yes' | 'no'): string | null {
  if (outcome === 'yes') return market.display.yesLabel ?? market.market.yesTeam;
  return market.display.noLabel;
}

/** `checkEntry`'s USD price messages, said in cents for the shared rail's cents box. */
const RAIL_PRICE_ERROR: Record<string, string> = {
  'Enter a price in dollars, like 0.40.': 'Enter a price in cents, like 40.',
  'Price must be between $0.01 and $0.99.': 'Price must be between 1¢ and 99¢.',
};

type RadioOption<T extends string> = { value: T; label: string; allowed?: boolean; tone?: 'positive' | 'negative' };

/**
 * A single-choice group with the standard radio keyboard model: one tab stop
 * (the chosen option, else the first allowed one) and arrow keys that move and
 * select. A not-allowed option stays visible with its reason but is skipped.
 */
function RadioGroup<T extends string>({ field, options, value, onChange, error, wrap }: {
  field: UsTicketField; options: RadioOption<T>[]; value: T | null; onChange(value: T): void; error?: string; wrap?: boolean;
}) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const allowed = options.filter((o) => o.allowed !== false);
  const stop = value !== null && allowed.some((o) => o.value === value) ? value : allowed[0]?.value;
  function onKeyDown(e: KeyboardEvent, index: number) {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!step || allowed.length === 0) return;
    e.preventDefault();
    for (let n = 1; n <= options.length; n++) {
      const at = (index + step * n + options.length) % options.length;
      if (options[at].allowed === false) continue;
      onChange(options[at].value);
      buttons.current[at]?.focus();
      return;
    }
  }
  return (
    <div role="radiogroup" aria-labelledby={`us-ticket-${field}-label`}
      aria-describedby={error ? `us-ticket-${field}-error` : undefined}
      className={cn('flex gap-1.5', wrap && 'flex-wrap')}>
      {options.map((o, i) => (
        <button key={o.value} ref={(el) => { buttons.current[i] = el; }} type="button" role="radio"
          aria-checked={value === o.value} aria-disabled={o.allowed === false || undefined}
          tabIndex={o.value === stop ? 0 : -1}
          className={cn(choice(value === o.value, o.tone), wrap && 'flex-none')}
          onKeyDown={(e) => onKeyDown(e, i)}
          onClick={() => o.allowed !== false && onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Field({ label, hint, hintWarn = false, hintTitle, headerRight, kpLabel = false, error, id, children }: {
  label: string; hint?: string; hintWarn?: boolean; hintTitle?: string; error?: string; id: UsTicketField; children: ReactNode;
  /** A control at the label row's right, as the K/P ticket's "+ N" beside Qty. */
  headerRight?: ReactNode;
  /** The K/P ticket's label size (the shared Game Detail rail). */
  kpLabel?: boolean;
}) {
  return (
    <div className="min-w-0" data-field={id}>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-x-2">
        <span className={cn('text-muted-foreground', kpLabel ? 'text-[10px]' : 'text-[11px] font-medium')} id={`us-ticket-${id}-label`}>{label}</span>
        {headerRight}
        {hint && (
          <span title={hintTitle}
            className={cn('text-[10px]', hintWarn ? 'text-amber-500 light:text-amber-700 font-medium' : 'text-muted-foreground')}>
            {hint}
          </span>
        )}
      </div>
      {children}
      {/* Match the shared ticket: no empty error row under every control.
          Actual errors remain beside their field. */}
      <p id={`us-ticket-${id}-error`} role={error ? 'alert' : undefined} aria-hidden={error ? undefined : true}
        data-testid={`us-ticket-error-${id}`}
        className={cn('mt-0.5 break-words text-[11px] leading-4 text-amber-500 light:text-amber-700', !error && 'hidden')}>
        {error ?? '·'}
      </p>
    </div>
  );
}

type Props = {
  inspectionOnly?: boolean;
  railControls?: UsRailControls;
  nowMs?: number;
  /** A Close may preset an immediate duration after its market rules arrive. */
  closing?: boolean;
  market: UsSelectedMarket;
  entry: UsTicketEntry;
  /** Only fields the trader has touched show errors; untouched ones stay quiet. */
  shown: Partial<Record<UsTicketField, boolean>>;
  check: UsTicketCheck;
  permissions: { buy: boolean; sell: boolean };
  onChange(patch: Partial<UsTicketEntry>): void;
  /** The host bound the outcome (Game Detail): show it, never offer the other. */
  outcomeLocked?: boolean;
  /** An edit keeps the order's side: the other side is shown, not offered. */
  actionLocked?: boolean;
  /** An edit: how the duration and post-only compare with the original order's,
   * and the post-only field's quick reference. */
  termNotes?: { duration: UsEditTermNote; postOnly: UsEditTermNote; postOnlyHelp: string };
  /** Narrow Game Detail column; the shared rail uses cents and adjacent price/quantity. */
  compact?: boolean;
  /** The trader's default and quick sizes (Settings → Preferences), as the
   * K/P ticket offers them; each is floored at this market's minimum. */
  sizePresets?: { defaultContracts: number; quickSizes: readonly number[] };
  /** A host pick just wrote the price: the box plays the K/P ticket's brief glow. */
  priceFlashing?: boolean;
  /** The trader's Polymarket US default duration is Good to Start (#1247): when
   * it is unavailable the row says so as the Kalshi ticket does. */
  gtsPreferred?: boolean;
};

/** One chip per distinct applied size: a preset below the market minimum
 * applies the minimum, and `below` names the presets that were raised. */
function sizeChoices(presets: readonly number[], minimum: string): { size: string; below: number[] }[] {
  const min = Number(minimum);
  const chips = new Map<string, number[]>();
  for (const n of presets.filter((p) => Number.isFinite(p) && p > 0)) {
    const size = String(n < min ? minimum : n);
    chips.set(size, [...(chips.get(size) ?? []), ...(n < min ? [n] : [])]);
  }
  return [...chips].map(([size, below]) => ({ size, below }));
}

export function UsTicketFields({
  inspectionOnly = false,
  market, entry, shown, check, permissions, onChange, outcomeLocked = false, actionLocked = false, termNotes, closing = false, compact = false, sizePresets, railControls, nowMs = Number.POSITIVE_INFINITY,
  priceFlashing = false, gtsPreferred = false,
}: Props) {
  const { rules } = market;
  const sharedRail = compact && !!railControls;
  const start = railControls?.startTimeMs;
  const startExpiry = startExpiryText(start);
  const gtsAllowed = gtsAllowedFor(start, nowMs, rules);
  // Why Good to Start is disabled, said beside the control (the title alone is a reference).
  const gtsReason = gtsAllowed ? null
    : !rules.timeInForce.includes(GTD) ? 'This market offers no dated duration; Good to Start unavailable'
      : !startExpiry ? 'No scheduled start yet; Good to Start unavailable'
        : 'Game has started; Good to Start unavailable';
  const durationMode = entry.timeInForce === GTD ? (entry.expiry === startExpiry && startExpiry ? 'gts' : 'custom') : entry.timeInForce === 'TIME_IN_FORCE_GOOD_TILL_CANCEL' ? 'gtc' : null;
  // As on Kalshi: the trader's Good to Start default could not apply, so no
  // duration is preset and the row says why, in amber, until one is chosen.
  const gtsWanted = sharedRail && gtsPreferred && !gtsAllowed && entry.timeInForce === null;

  const error = (field: UsTicketField) => {
    const text = shown[field] ? check.fields[field] : undefined;
    // The shared rail's price box takes cents; its guidance says so. The
    // check's USD contract (and every other layout) is unchanged.
    return sharedRail && field === 'price' && text ? RAIL_PRICE_ERROR[text] ?? text : text;
  };
  const durations = durationOptions(rules);
  const outcomeUpper = entry.outcome?.toUpperCase();
  const previous = stepPrice(entry.outcomePrice.trim(), rules.priceTick, -1);
  const next = stepPrice(entry.outcomePrice.trim(), rules.priceTick, 1);
  const book = market.market.book;
  const quote = entry.outcome === 'yes'
    ? { bid: book.yesBidUsd, ask: book.yesAskUsd }
    : entry.outcome === 'no' ? { bid: book.noBidUsd, ask: book.noAskUsd } : null;

  const defaultChips = sizePresets ? sizeChoices([sizePresets.defaultContracts], rules.minimumQuantity).map(({ size, below }) => (
    <button key={`default-${size}`} type="button"
      title={below.length ? `Your default is ${below[0]}; this market’s minimum is ${size}, so this sets ${size}.`
        : `Set contracts to your default (${size}) — change in Settings → Preferences`}
      onClick={() => onChange({ quantity: size })}
      className="text-muted-foreground hover:text-foreground shrink-0 px-1 text-[10px] font-medium leading-none">
      + {size}{below.length ? ' (min)' : ''}
    </button>
  )) : null;
  const quickChips = sizePresets ? sizeChoices(sizePresets.quickSizes, rules.minimumQuantity).map(({ size, below }) => (
    <button key={size} type="button" data-us-quick-size
      title={below.length ? `Your quick size ${below.join(', ')} is below this market’s minimum of ${size}, so this sets ${size}.`
        : `Set contracts to ${size}`}
      onClick={() => onChange({ quantity: size })}
      className="bg-muted text-muted-foreground hover:text-foreground rounded-md px-1.5 py-0.5 text-[10px] font-semibold leading-none">{size}</button>
  )) : [];
  const quantityField = (
        <Field id="quantity" label={sharedRail ? "Qty" : "Contracts"} hint={compact ? undefined : `Min ${rules.minimumQuantity} · step ${rules.quantityStep}`} error={error('quantity')}
          kpLabel={sharedRail} headerRight={sharedRail && defaultChips ? <span className="flex items-center" data-testid="us-ticket-sizes">{defaultChips}</span> : undefined}>
          <input type="text" inputMode="decimal" aria-labelledby="us-ticket-quantity-label"
            aria-describedby={error('quantity') ? 'us-ticket-quantity-error' : undefined} aria-invalid={!!error('quantity')}
            value={entry.quantity} onChange={(e) => onChange({ quantity: e.target.value })}
            className="border-border bg-input text-foreground w-full rounded-md border px-2.5 py-1.5 font-mono text-xs tabular-nums" />
          {sizePresets && !sharedRail && (
            <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1" data-testid="us-ticket-sizes">
              {defaultChips}
              {quickChips}
            </div>
          )}
        </Field>
  );
  const priceField = (
        <Field id="price" kpLabel={sharedRail}
          label={sharedRail ? `Price (¢)${railControls?.priceLabelSuffix ? ` · ${railControls.priceLabelSuffix}` : ''}` : `Price (USD per ${outcomeUpper ?? 'outcome'} contract)`}
          hint={compact ? undefined : `Tick ${rules.priceTick}`} error={error('price')}>
          <div className="border-border bg-input flex rounded-md border">
            <button type="button" aria-label="One tick lower" disabled={previous === null}
              onClick={() => previous && onChange({ outcomePrice: previous })}
              className="text-muted-foreground hover:text-foreground flex w-7 shrink-0 items-center justify-center disabled:opacity-30">
              <Minus size={12} />
            </button>
            {sharedRail ? <CentsInput type="text" inputMode="decimal" aria-labelledby="us-ticket-price-label"
              aria-describedby={error('price') ? 'us-ticket-price-error' : undefined} aria-invalid={!!error('price')}
              value={entry.outcomePrice} placeholder="0" onPriceChange={outcomePrice => onChange({outcomePrice})}
              onKeyDown={(e) => {
                const step = e.key === 'ArrowUp' ? next : e.key === 'ArrowDown' ? previous : undefined;
                if (step === undefined) return;
                e.preventDefault();
                if (step) onChange({ outcomePrice: step });
              }}
              className={cn('text-foreground min-w-0 flex-1 border-x border-border bg-transparent px-2 py-1.5 text-center font-mono text-xs tabular-nums outline-none', priceFlashing && 'animate-price-flash')} /> : <input type="text" inputMode="decimal" aria-labelledby="us-ticket-price-label"
              aria-describedby={error('price') ? 'us-ticket-price-error' : undefined} aria-invalid={!!error('price')}
              value={entry.outcomePrice} placeholder="0.00" onChange={e => onChange({outcomePrice:e.target.value})}
              onKeyDown={e => { const step = e.key === 'ArrowUp' ? next : e.key === 'ArrowDown' ? previous : undefined; if (step === undefined) return; e.preventDefault(); if (step) onChange({outcomePrice:step}); }}
              className={cn('min-w-0 flex-1 bg-transparent px-1 py-1.5 text-center text-xs tabular-nums outline-none', priceFlashing && 'animate-price-flash')} />}
            <button type="button" aria-label="One tick higher" disabled={next === null}
              onClick={() => next && onChange({ outcomePrice: next })}
              className="text-muted-foreground hover:text-foreground flex w-7 shrink-0 items-center justify-center disabled:opacity-30">
              <Plus size={12} />
            </button>
          </div>
        </Field>
  );

  const postOnlyField = (
      <Field id="postOnly" label="Post-only" hintWarn={termNotes?.postOnly.warn}
        hintTitle={termNotes?.postOnlyHelp}
        hint={termNotes ? termNotes.postOnly.text : !rules.supportsPostOnly ? 'Not supported' : !compact ? 'Default: off' : undefined}
        error={railControls ? undefined : error('postOnly')}>
        <label className={cn('flex items-center gap-2 text-xs', !rules.supportsPostOnly && 'opacity-50')}>
          <input type="checkbox" checked={entry.postOnly} disabled={!rules.supportsPostOnly && !entry.postOnly}
            onChange={(e) => onChange({ postOnly: e.target.checked })} />
          {compact ? (entry.postOnly ? 'Only rest; never take liquidity' : 'May fill immediately') : (entry.postOnly ? 'On — the order only rests; it never takes liquidity' : 'Off — the order may fill immediately')}
        </label>
      </Field>
  );

  return (
    <div className={cn('grid min-w-0', sharedRail ? 'gap-3' : 'gap-2')}>
      {/* A host-bound outcome is already named by the host and by Buy/Sell below. */}
      {!outcomeLocked && <Field id="outcome" label="Outcome" error={error('outcome')}>
        <RadioGroup field="outcome" value={entry.outcome} error={error('outcome')} onChange={(outcome) => onChange({ outcome })}
          options={(['yes', 'no'] as const).map((o) => {
            const name = outcomeName(market, o);
            return { value: o, label: `${o.toUpperCase()}${name ? ` · ${name}` : ''}`, tone: o === 'yes' ? 'positive' : 'negative' };
          })} />
      </Field>}

      {railControls ? (
        <>
          <div className="space-y-1.5">
          <div role="group" className="flex gap-1.5" aria-label="Buy side">
            {(['yes','no'] as const).map(side => <button key={side} type="button"
              disabled={!permissions.buy} aria-pressed={railControls.side === side && entry.action === 'buy'}
              className={choice(railControls.side === side && entry.action === 'buy', side === 'yes' ? 'positive' : 'negative')}
              onClick={() => { if (side !== railControls.side) railControls.onSideChange(side); else onChange({action:'buy'}); }}>
              BUY {side.toUpperCase()}
            </button>)}
          </div>
          {/* As K/P's "(GSW wins)": what the selected side backs; the native binding is its title. */}
          {railControls.sideCaption && (
            <p className="text-muted-foreground break-words text-center text-[11px] [overflow-wrap:anywhere]"
              data-testid="us-ticket-side-caption" title={railControls.sideCaption.title}>
              {railControls.sideCaption.text}
            </p>
          )}
          </div>
          <div className="flex gap-1.5">
            <span className="bg-primary text-primary-foreground flex-1 rounded-md py-1 text-center text-[11px] font-semibold">LIMIT</span>
            <button type="button" disabled title="This US ticket currently submits limit orders. Immediate limits are available under More order options."
              className="bg-muted/40 text-muted-foreground/40 flex-1 cursor-not-allowed rounded-md py-1 text-[11px] font-semibold">MARKET</button>
          </div>
          {/* The side's problem is said here, in the open; the folded Buy/Sell choice below only offers the other side. */}
          {error('action') && <p role="alert" className="text-xs text-amber-500" data-testid="us-ticket-rail-action-error">{error('action')}</p>}
          <div role="group" className="flex gap-1.5" aria-label="Order duration" data-us-duration>
            {(['gts','gtc','custom'] as const).map(mode => <button type="button" key={mode}
              disabled={mode === 'gts' ? !gtsAllowed : !rules.timeInForce.includes(mode === 'gtc' ? 'TIME_IN_FORCE_GOOD_TILL_CANCEL' : GTD)}
              aria-pressed={durationMode === mode}
              className={cn(choice(durationMode === mode),'px-1 py-1 text-[11px] disabled:cursor-not-allowed disabled:opacity-40',
                mode === 'gts' && gtsWanted && 'bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-500/40 light:text-amber-700 disabled:opacity-100')}
              title={mode === 'gts' ? (gtsReason ?? `Expires ${entry.expiry ? entry.expiry.replace('T', ' ') + ' UTC' : 'at the scheduled event start'}; later schedule changes do not move a placed order.`) : mode === 'gtc' ? 'Rests until canceled or settled.' : 'Choose an explicit expiry in your local time.'}
              onClick={() => onChange({timeInForce: mode === 'gtc' ? 'TIME_IN_FORCE_GOOD_TILL_CANCEL' : GTD, expiry: mode === 'gts' ? startExpiry : mode === 'custom' ? '' : entry.expiry})}>
              {mode === 'gts' ? 'GOOD TO START' : mode === 'gtc' ? 'GOOD TO CLOSE' : 'CUSTOM'}
            </button>)}
          </div>
          {gtsWanted ? (
            <p role="alert" className="text-[11px] leading-snug text-amber-300 light:text-amber-700" data-testid="us-ticket-gts-reason">
              {gtsUnavailableNote(gtsReason)}
            </p>
          ) : entry.timeInForce === null && <p className="text-[11px] text-muted-foreground">Choose an order duration.</p>}
          {error('duration') && <p role="alert" className="text-xs text-amber-500">{error('duration')}</p>}
          {durationMode === 'gts' && error('expiry') && <p role="alert" className="text-[11px] text-amber-500">{error('expiry')}</p>}
        </>
      ) : (<Field id="action" label="Buy or sell" error={error('action')}>
        <RadioGroup field="action" value={entry.action} error={error('action')} onChange={(action) => onChange({ action })}
          options={(['buy', 'sell'] as const).map((a) => ({
            value: a,
            allowed: permissions[a] && (!actionLocked || a === entry.action),
            label: `${a === 'buy' ? 'Buy' : 'Sell'}${outcomeUpper ? ` ${outcomeUpper}` : ''}${permissions[a] ? '' : ' — not permitted'}`,
          }))} />
      </Field>)}

      <div className={cn('grid min-w-0 gap-2', compact ? 'grid-cols-2' : 'sm:grid-cols-2')}>
        {sharedRail ? priceField : quantityField}
        {sharedRail ? quantityField : priceField}
        {/* As the K/P ticket's quick row: full width, under both fields. */}
        {sharedRail && quickChips.length > 0 && (
          <div className="col-span-2 -mt-1 flex items-center gap-1.5" data-us-quick-size-row>
            <span className="text-muted-foreground mr-auto text-[10px]">Quick</span>
            {quickChips}
          </div>
        )}
      </div>
      {/* Reserved line: appears with the outcome without moving the fields below.
          The shared rail has the live ladder beside it; the Tracker's Edit and
          Close windows (also compact) have no other bid/ask guidance. */}
      {!sharedRail && <p className={cn('text-muted-foreground -mt-1 text-[11px]', !quote && 'invisible')} data-testid="us-ticket-quote">
        {quote ? `${outcomeUpper} bid ${quote.bid ?? 'unavailable'} · ask ${quote.ask ?? 'unavailable'} (quote at market load; not live)` : '·'}
      </p>}

      {!railControls && <Field id="duration" label="Duration" hintWarn={termNotes?.duration.warn}
        hintTitle={termNotes ? 'Starts from the original order’s duration. Duration: when Polymarket US cancels the unfilled part.'
          : 'Duration: when Polymarket US cancels the unfilled part.'}
        hint={termNotes ? termNotes.duration.text : closing ? 'Review duration — Close may preset it' : 'No default — choose one'} error={error('duration')}>
        {durations.length === 0 ? (
          <p className="text-xs">This market offers no duration this ticket supports.</p>
        ) : (
          <RadioGroup field="duration" wrap value={entry.timeInForce} error={error('duration')}
            onChange={(timeInForce) => onChange({ timeInForce })}
            options={durations.map((d) => ({ value: d.value, label: d.label }))} />
        )}
      </Field>}

      {entry.timeInForce === GTD && (!railControls || durationMode !== 'gts') && (
        // The rail takes local time, as the K/P tickets do; the entry stays UTC.
        <Field id="expiry" label={railControls ? 'Expires at (local)' : 'Expires at (UTC)'} error={error('expiry')}>
          <input type="datetime-local" step={1} aria-labelledby="us-ticket-expiry-label"
            aria-describedby={error('expiry') ? 'us-ticket-expiry-error' : undefined} aria-invalid={!!error('expiry')}
            value={railControls ? utcExpiryToLocal(entry.expiry) : entry.expiry}
            onChange={(e) => onChange({ expiry: railControls ? localExpiryToUtc(e.target.value) : e.target.value })}
            className="border-border bg-input w-full rounded-md border px-2 py-1.5 text-xs" />
        </Field>
      )}

      {compact && !railControls && <p className="text-muted-foreground text-[11px]">US: {rules.minimumQuantity}-contract minimum · step {rules.quantityStep} · limit orders.</p>}
      {/* A non-default term inside the closed options is named on the summary, so it is never hidden. */}
      {railControls && <details className="text-xs">
        <summary className="cursor-pointer text-[10px] text-muted-foreground">More order options{entry.action === 'sell' ? ' · Sell' : ''}{entry.timeInForce?.includes('IMMEDIATE') ? ' · IOC' : entry.timeInForce?.includes('FILL_OR_KILL') ? ' · FOK' : ''}{entry.postOnly ? ' · Post-only' : ''}</summary>
        <div className="space-y-2 py-2">
          <p className="text-muted-foreground text-[11px]">Minimum {rules.minimumQuantity} contracts; quantity step {rules.quantityStep}. Totals include the estimated taker fee; the venue charges the actual fee.</p>
          {durationMode === 'gts' && entry.expiry && <p className="text-muted-foreground text-[11px]">Expires {entry.expiry.replace('T', ' ')} UTC; later schedule changes do not move a placed order.</p>}
          <Field id="action" label="Buy or sell">
            <RadioGroup field="action" value={entry.action} onChange={action => onChange({action})}
              options={(['buy','sell'] as const).map(action => ({value:action,label:`${action === 'buy' ? 'Buy' : 'Sell'} ${outcomeUpper}`,allowed:permissions[action]}))}/>
          </Field>
          {/* The duration row above already announces its problem; this list only adds the immediate durations. */}
          <Field id="duration" label="Duration">
            <RadioGroup field="duration" wrap value={entry.timeInForce} onChange={timeInForce => onChange({timeInForce})} options={durations}/>
          </Field>
          {postOnlyField}
        </div>
      </details>}
      {/* The post-only problem is said in the open; the field itself may be folded. */}
      {railControls && error('postOnly') && <p role="alert" className="text-xs text-amber-500">{error('postOnly')}</p>}
      {!railControls && postOnlyField}
      {/* One venue line, as the Polymarket ticket's. */}
      {sharedRail && (
        <p className="text-muted-foreground text-[10px] leading-snug" data-testid="us-ticket-venue-note"
          title={`Quantity step ${rules.quantityStep}. Immediate limits and post-only are under More order options. ${inspectionOnly ? 'Native fees and account effects remain unconfirmed.' : 'Totals include the estimated taker fee; the venue charges the actual fee.'}`}>
          Polymarket US: {rules.minimumQuantity}-contract minimum · limit orders only · {inspectionOnly ? 'fees unconfirmed.' : 'estimated fees included.'}
        </p>
      )}
    </div>
  );
}

/** The Kalshi ticket's Good-to-Start note, with the reason this market gives. */
function gtsUnavailableNote(reason: string | null): string {
  const why = reason === 'This market offers no dated duration; Good to Start unavailable'
    ? 'this market offers no dated duration' : 'a trustworthy future event start is not available';
  return `Good to Start is unavailable because ${why}. Choose Good to Close explicitly to place without an expiry.`;
}
